import { Card } from '@app/components/various/Card';
import { compiledPaths } from '@app/paths';
import { usePageOptions } from '@app/utils/hooks/pages/usePageOptions';
import { useTableColumnsConfiguration } from '@app/utils/hooks/table/useTableColumnsConfiguration';
import { Row } from '@tanstack/react-table';
import { Cms, Id } from '@typings';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { LanguagesCell } from '../cells';

import { PreviouslyTooltip } from './PreviouslyTooltip';

interface Props {
  row: Row<Cms.Page>;
  statusFilter: Cms.PageStatus | '';
  setEditingPage: (id: Id) => void;
  openSharePage: (id: Id) => void;
  duplicatePage: ({ pageId }: { pageId: Id }) => void;
}

export const PagesListCard = ({ row, statusFilter, setEditingPage, openSharePage, duplicatePage }: Props) => {
  const { columnsVisibility } = useTableColumnsConfiguration({ tableName: 'pages' });
  const { t } = useTranslation(['common', 'cms']);
  const { name, languages, slug, description, markets, createdDate, lastModifiedDate, lastPublishedDate, id } = row.original;
  const menuItems = usePageOptions({ duplicatePage, openSharePage, page: row?.original, setEditingPage });

  return (
    <Card.Wrapper>
      {columnsVisibility.languages?.isVisible && (
        <Card.Header>
          <span className="z-interactiveTableItem">
            <LanguagesCell languages={languages ?? []} row={row.original} filter={statusFilter} />
          </span>
        </Card.Header>
      )}
      <Card.Title>
        <PreviouslyTooltip page={row.original}>
          <Link to={compiledPaths.CMS_EDITOR({ pageId: id })} className="absolute-cursor-pointer">
            {name}
          </Link>
        </PreviouslyTooltip>
      </Card.Title>
      <Card.Link to={slug} className="z-interactiveTableItem">
        /{slug}
      </Card.Link>
      <Card.AttributeList>
        {columnsVisibility.description?.isVisible && <Card.Attribute value={description || '-'} name={t('common:description')} />}
        {columnsVisibility.markets?.isVisible && (
          <Card.Attribute value={markets.map(market => market.name).join(', ')} name={t('cms:market_other')} />
        )}
        {columnsVisibility.createdDate?.isVisible && <Card.Attribute value={createdDate} name={t('common:created')} />}
        {columnsVisibility.lastModifiedDate?.isVisible && <Card.Attribute value={lastModifiedDate} name={t('common:last_modified')} />}
        {columnsVisibility.lastPublishedDate?.isVisible && (
          <Card.Attribute value={lastPublishedDate || '-'} name={t('common:last_published')} />
        )}
      </Card.AttributeList>
      <Card.Menu title={name} items={menuItems} className="z-interactiveTableItem" />
    </Card.Wrapper>
  );
};
